import React, {Component, ReactElement, ReactNode} from 'react'
import NatMessageRepo from '../message/repository/NatMessageRepo'
import {MessageInfo, NatGetMessageList, SearchCondition} from './repository/NatGetMessageList'
import {MESSAGE_CATEGORY, VALID_OR_NOT_CHECK} from '../../../CodeMaster'
import {SortOrder} from '../../../components/shared/SortOrder'
import NatCurrentDateTimeRepo from '../currentDateTime/repository/NatCurrentDateTimeRepo'
import {NatGetCurrentDateTime} from '../currentDateTime/repository/NatGetCurrentDateTime'
import {formatDateSlashSeparator, formatForDateInput} from '../../../formatters/formatDate'
import {Nullable} from '../../../app/TypeHelpers'
import {StyledDiv} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {BreakPoint, Colors, FontSize} from '../../../styledComponents/Constants'
import {generateMedia} from 'styled-media-query'
import {CardActionArea} from '@mui/material'
import {NatAsyncCallFunction} from '../../common/util/Natutil'
import {createResidentPage, ResidentPagePropsSet} from '../../../components/shared/hoc/createResidentPage'
import {getMessageCategoryColor} from './common/NatMessageCategoryColorHelper'
import {PageTitle} from '../applications/common/resident/NatResidentApplicationsCommon'
import {BlockSpacer} from '../../../styledComponents/ResponsiveConvenience'
import {faThumbtack} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import ScrollableCategorySelector from '../../../components/shared/ScrollableCategorySelector'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {Location} from 'react-router-dom'

const KEY_SELECTED_MESSAGE_CATEGORY_IDS = 'NatResidentMessageListPage.selectedMessageCategoryIds'

interface Props {
    messageRepo: NatMessageRepo,
    currentDateTimeRepo: NatCurrentDateTimeRepo,
    location: Location,
}

interface States {
    categoryListInAllMessageList: MESSAGE_CATEGORY[]
    selectedCategoryList: MESSAGE_CATEGORY[]
    allMessageList: MessageInfo[]
    isLoadCompleted: boolean
}

function getMessageCategoryLabel(categoryId: string): Nullable<ReactElement> {
    let messageCategoryLabel: any
    messageCategoryLabel = MESSAGE_CATEGORY.codeToEnumConstants(categoryId)
    if (messageCategoryLabel) {
        return (
            <>{messageCategoryLabel.label}</>
        )
    } else {
        return null
    }
}

export const NatResidentMessageListItemComponent = (props: {
    onClick: () => void,
    messageInfo: MessageInfo,
}) => {
    return <MessageActionArea onClick={props.onClick}>
        <MessageArea>
            <MessageHeader>
                <PublishDateTime>
                    {formatDateSlashSeparator(props.messageInfo.publishStartDate)}
                </PublishDateTime>
                <CategoryLabel categoryId={props.messageInfo.categoryId}>
                    {getMessageCategoryLabel(props.messageInfo.categoryId)}
                </CategoryLabel>
                {(props.messageInfo.topFixed === VALID_OR_NOT_CHECK.VALID.value) &&
                    <TopFixed>
                        <FontAwesomeIcon icon={faThumbtack} size={'sm'}/>
                    </TopFixed>
                }
            </MessageHeader>
            <MessageTitle>
                {props.messageInfo.title}
            </MessageTitle>
        </MessageArea>
    </MessageActionArea>
}

class NatResidentMessageListPage extends Component<Props & ResidentPagePropsSet, States> {
    constructor(props: Props & ResidentPagePropsSet) {
        super(props)
        this.state = {
            categoryListInAllMessageList: [],
            selectedCategoryList: [],
            allMessageList: [],
            isLoadCompleted: false,
        }

        this.onChangeCategory = this.onChangeCategory.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const currentDateTime: Nullable<NatGetCurrentDateTime> =
                await NatAsyncCallFunction(
                    this.props.currentDateTimeRepo.getCurrentDateTime.bind(
                        this.props.currentDateTimeRepo), this.props.navigate)
            if (currentDateTime && currentDateTime.details) {
                const currentDateString = formatForDateInput(`${currentDateTime.details.currentDateTime}+09:00`)
                await this.loadAllMessageList(currentDateString)
            }
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async loadAllMessageList(currentDateString: string): Promise<void> {
        const searchCondition: SearchCondition = {
            extractStart: currentDateString,
            extractEnd: currentDateString,
        }
        const searchConditionSortInfo = [
            new SortOrder('topFixed', 'desc'),
            new SortOrder('publishStartDate', 'desc'),
            new SortOrder('updateDatetime', 'desc'),
        ]
        const response: Nullable<NatGetMessageList> =
            await NatAsyncCallFunction(
                this.props.messageRepo.getMessageList.bind(
                    this.props.messageRepo,
                    searchCondition,
                    undefined,
                    searchConditionSortInfo,
                ),
                this.props.navigate)
        if (response && response.details) {
            const categoryListInAllMessageList = this.getCategoryListInAllMessageList(response.details.messageInfo)
            const selectedCategoryList = this.getSelectedCategoryListFromSession(categoryListInAllMessageList)
            this.setState({
                allMessageList: response.details.messageInfo,
                categoryListInAllMessageList: categoryListInAllMessageList,
                selectedCategoryList: selectedCategoryList,
            })
        }
    }

    onChangeCategory(changeCategoryList: MESSAGE_CATEGORY[]): void {
        this.setSelectedCategoryListToSession(changeCategoryList)

        this.setState({
            selectedCategoryList: changeCategoryList,
        })
    }

    private getCategoryListInAllMessageList(allMessageList: MessageInfo[]): MESSAGE_CATEGORY[] {
        const categoryIdList = allMessageList
            .map(message => message.categoryId)

        return MESSAGE_CATEGORY.values()
            .filter(message => categoryIdList.includes(message.value))
    }

    private getSelectedCategoryListFromSession(categoryListInAllMessageList: MESSAGE_CATEGORY[]): MESSAGE_CATEGORY[] {
        const json = SessionStorageHelper.getStringItemOrNull(KEY_SELECTED_MESSAGE_CATEGORY_IDS)
        if (!json) {
            return []
        }

        const selectedCategoryIdsFromSession: string[] = JSON.parse(json)
        return categoryListInAllMessageList
            .filter(category => selectedCategoryIdsFromSession.includes(category.value))
    }

    private setSelectedCategoryListToSession(selectedCategoryList: MESSAGE_CATEGORY[]): void {
        const selectedCategoryIdList = selectedCategoryList.map(category => category.value)

        SessionStorageHelper.setItem(KEY_SELECTED_MESSAGE_CATEGORY_IDS, JSON.stringify(selectedCategoryIdList))
    }

    private filterMessageList(
        allMessageList: MessageInfo[],
        selectedCategoryList: MESSAGE_CATEGORY[],
    ): MessageInfo[] {
        if (selectedCategoryList.length === 0) {  // すべての場合
            return allMessageList
        }

        const selectedCategoryIdList: string[] = selectedCategoryList
            .map(category => category.value)

        return allMessageList
            .filter(message => selectedCategoryIdList.includes(message.categoryId))
    }

    onClickMessage = (messageId: string) => (): void => {
        this.props.navigate(`/resident/message/${messageId}`, {state: {previousPageUrl: '/resident/messageList'}})
    }

    private renderMessageList(): ReactNode {
        const viewMessageList = this.filterMessageList(
            this.state.allMessageList,
            this.state.selectedCategoryList,
        )

        return <>
            <BlockSpacer height={'10px'}/>
            <CategorySelectorArea>
                <ScrollableCategorySelector
                    onChangeCategory={this.onChangeCategory}
                    categoryList={this.state.categoryListInAllMessageList}
                    selectedCategoryList={this.state.selectedCategoryList}
                    displayAllButton
                    newDesign
                />
            </CategorySelectorArea>
            <BlockSpacer height={'10px'}/>
            <MainContainer>
                {viewMessageList.map((messageInfo) => (
                    <NatResidentMessageListItemComponent
                        onClick={this.onClickMessage(messageInfo.messageId)}
                        messageInfo={messageInfo}/>
                ))}
            </MainContainer>
            <BlockSpacer height={'15px'}/>
        </>
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return <>
            <BlockSpacer height={'15px'}/>
            <PageTitle>メッセージ一覧</PageTitle>
            {(this.state.allMessageList.length > 0) ?
                this.renderMessageList()
                :
                <>
                    <BlockSpacer height={'20px'}/>
                    <NoMessage>メッセージはありません</NoMessage>
                </>
            }
        </>
    }
}

export default createResidentPage(NatResidentMessageListPage)

// ブレイクポイント
const breakPoint: any = generateMedia({
    sp: `${BreakPoint.Sp}`,
})

interface CategoryColorProps {
    categoryId: string;
}

const CategorySelectorArea: StyledDiv = styled.div`
  margin: 0 -15px;
`

const MainContainer: StyledDiv = styled.div`
  background-color: ${Colors.Surface};

  & > button:first-child > div {
    border-top: none;
  }

  ${breakPoint.lessThan('sp')`
    margin: 0 -15px;
`}
`

const MessageActionArea = styled(CardActionArea)`
  &.MuiCardActionArea-root {
    padding: 0 15px;
  }
`

const MessageArea: StyledDiv = styled.div`
  border-top: 1px solid ${Colors.AccentLight};
  padding-top: 7px;
  padding-bottom: 9px;
`

const MessageHeader: StyledDiv = styled.div`
  display: flex;
  height: 24px;
  align-items: center;
`

const PublishDateTime: StyledDiv = styled.div`
  width: 72px;
  font-size: ${FontSize.Small};
  color: ${Colors.AccentDark};
`

const TopFixed: StyledDiv = styled.div`
  margin-left: 8px;
  color: ${Colors.AccentDark};
`

const MessageTitle: StyledDiv = styled.div`
  margin-top: 2px;
  font-size: ${FontSize.Middle};
  color: ${Colors.Body};
  line-height: 1.5;
`

const NoMessage: StyledDiv = styled.div`
  color: ${Colors.Body};
  text-align: center;

  ${breakPoint.lessThan('sp')`
    text-align: left;
`}

`

export const CategoryLabel = styled.div<CategoryColorProps>`
  color: #fff;
  font-size: 8px;
  line-height: 2.0;
  background-color: ${(props: CategoryColorProps) => getMessageCategoryColor(props.categoryId)};
  height: 16px;
  width: 56px;
  display: block;
  text-align: center;
`
